import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Logo } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setMessage("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setMessage(data.message);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Reset Your Password" testid="forgot-page">
      <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
        {error && <p className="bg-red-500/10 border border-red-500/40 text-red-400 text-sm rounded-sm px-4 py-3" data-testid="forgot-error">{error}</p>}
        {message && <p className="bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-sm rounded-sm px-4 py-3" data-testid="forgot-success">{message}</p>}
        <div className="space-y-2">
          <Label htmlFor="email" className="text-slate-600">Email</Label>
          <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="bg-obsidian border-steel text-charcoal h-12" data-testid="forgot-email-input" />
        </div>
        <Button type="submit" disabled={loading}
          className="w-full bg-lomond text-black font-heading font-extrabold uppercase hover:bg-[#E6B400] h-12"
          data-testid="forgot-submit-btn">
          {loading ? "Sending..." : "Send Reset Link"}
        </Button>
        <p className="text-center text-sm text-slate-500">
          <Link to="/login" className="text-lomond hover:underline font-semibold" data-testid="back-to-login">Back to login</Link>
        </p>
      </form>
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [invalidLink, setInvalidLink] = useState(!token);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) { setError("Passwords do not match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      const msg = apiError(err);
      if (/no longer valid/i.test(msg)) setInvalidLink(true);
      else setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <AuthShell title="Password Updated" testid="reset-page">
        <div className="space-y-5 text-center" data-testid="reset-success">
          <p className="bg-emerald-500/10 border border-emerald-500/40 text-emerald-300 text-sm rounded-sm px-4 py-3">
            Your password has been changed successfully.
          </p>
          <Link to="/login">
            <Button className="w-full bg-lomond text-black font-heading font-extrabold uppercase hover:bg-[#E6B400] h-12"
              data-testid="return-to-login-btn">
              Return to Login
            </Button>
          </Link>
        </div>
      </AuthShell>
    );
  }

  if (invalidLink) {
    return (
      <AuthShell title="Reset Your Password" testid="reset-page">
        <div className="space-y-5 text-center" data-testid="reset-invalid">
          <p className="bg-red-500/10 border border-red-500/40 text-red-400 text-sm rounded-sm px-4 py-3">
            This password reset link is no longer valid.
          </p>
          <Link to="/forgot-password">
            <Button className="w-full bg-lomond text-black font-heading font-extrabold uppercase hover:bg-[#E6B400] h-12"
              data-testid="request-new-link-btn">
              Request a New Link
            </Button>
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Choose a New Password" testid="reset-page">
      <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
        {error && <p className="bg-red-500/10 border border-red-500/40 text-red-400 text-sm rounded-sm px-4 py-3" data-testid="reset-error">{error}</p>}
        <div className="space-y-2">
          <Label htmlFor="password" className="text-slate-600">New Password</Label>
          <Input id="password" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
            className="bg-obsidian border-steel text-charcoal h-12" data-testid="reset-password-input" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm" className="text-slate-600">Confirm New Password</Label>
          <Input id="confirm" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
            className="bg-obsidian border-steel text-charcoal h-12" data-testid="reset-confirm-input" />
        </div>
        <Button type="submit" disabled={loading}
          className="w-full bg-lomond text-black font-heading font-extrabold uppercase hover:bg-[#E6B400] h-12"
          data-testid="reset-submit-btn">
          {loading ? "Updating..." : "Reset Password"}
        </Button>
      </form>
    </AuthShell>
  );
}

function AuthShell({ title, testid, children }) {
  return (
    <div className="min-h-screen bg-obsidian flex flex-col" data-testid={testid}>
      <div className="hazard-stripes h-2" />
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <Link to="/" className="inline-block"><Logo /></Link>
            <h1 className="font-heading font-extrabold uppercase tracking-tight text-charcoal text-2xl mt-6">{title}</h1>
          </div>
          <div className="bg-slate-panel border border-steel rounded-sm p-6 sm:p-8">{children}</div>
        </div>
      </div>
    </div>
  );
}
